/**
 * Shared constants for the Team & Capacity Planner v2.
 * Extracted from CapacityPlanner.jsx to keep the main file lean.
 */

export const STATUS_META = {
  new_request:          { label: "New request",       dot: "bg-amber-400"   , rowTint: "bg-amber-200/70" },
  site_visit_needed:    { label: "Site visit",        dot: "bg-slate-400"   , rowTint: "" },
  waiting_for_estimate: { label: "Needs estimate",    dot: "bg-orange-500"  , rowTint: "bg-orange-500/5" },
  offer_sent:           { label: "Offer sent",        dot: "bg-sky-500"     , rowTint: "bg-sky-500/5" },
  waiting_for_customer: { label: "Waiting for an answer", dot: "bg-sky-400" , rowTint: "bg-sky-200/60" },
  accepted:             { label: "Accepted",          dot: "bg-emerald-400" , rowTint: "" },
  ready_to_schedule:    { label: "Ready to plan",     dot: "bg-cyan-500"    , rowTint: "" },
  not_started:          { label: "Not Started",       dot: "bg-slate-400"   , rowTint: "bg-slate-200/60" },
  noted:                { label: "Noted",             dot: "bg-blue-400"    , rowTint: "bg-blue-200/60" },
  tentative:            { label: "Tentative",         dot: "bg-amber-500"   , rowTint: "" },
  confirmed:            { label: "Confirmed",         dot: "bg-emerald-500" , rowTint: "" },
  in_daily_plan:        { label: "In daily plan",     dot: "bg-emerald-600" , rowTint: "" },
  in_progress:          { label: "In Progress",       dot: "bg-yellow-400"  , rowTint: "bg-yellow-200/70" },
  paused:               { label: "Paused",            dot: "bg-orange-400"  , rowTint: "bg-orange-500/5" },
  completed:            { label: "Complete",          dot: "bg-emerald-500" , rowTint: "bg-emerald-200/60" },
  rejected:             { label: "Rejected",          dot: "bg-red-500"     , rowTint: "bg-slate-800/40 opacity-60" },
  cancelled:            { label: "Cancelled",         dot: "bg-slate-500"   , rowTint: "bg-slate-800/40 opacity-60" },
  // Recurring inspection statuses — auto-generate a new schedule every N days
  // for the current + next week. Handled server-side in weeks materialization.
  kontroll_7d:          { label: "Kontroll · 7 dager",  dot: "bg-cyan-400"    , rowTint: "bg-cyan-500/5",  recurrence: 7  },
  kontroll_14d:         { label: "Kontroll · 14 dager", dot: "bg-cyan-500"    , rowTint: "bg-cyan-500/5",  recurrence: 14 },
  kontroll_21d:         { label: "Kontroll · 21 dager", dot: "bg-cyan-600"    , rowTint: "bg-cyan-500/5",  recurrence: 21 },
  kontroll_30d:         { label: "Kontroll · 30 dager", dot: "bg-cyan-700"    , rowTint: "bg-cyan-500/5",  recurrence: 30 },
};

// The statuses shown in the pickers by default — the Smartsheet-aligned set the
// team actually uses, plus the recurring Kontroll ones. The rest stay defined
// above (so old data still renders) but are hidden to reduce clutter; pass
// showAll=true to reveal them.
export const STATUS_KEYS_ACTIVE = [
  "new_request", "not_started", "in_progress", "noted",
  "waiting_for_customer", "completed",
  "kontroll_7d", "kontroll_14d", "kontroll_21d", "kontroll_30d",
];

// Build a picker option list: the active set, always including the row's
// current status (so editing a legacy row never loses its value), unless
// showAll is requested (then every defined status is returned).
export const statusOptions = (current, showAll = false) => {
  if (showAll) return Object.keys(STATUS_META);
  const keys = [...STATUS_KEYS_ACTIVE];
  if (current && !keys.includes(current) && STATUS_META[current]) keys.push(current);
  return keys;
};

export const STATUS_BUCKET_STYLE = {
  available:   { ring: "#10b981", chip: "bg-emerald-500/15 text-emerald-300", label: "Available" },
  busy:        { ring: "#eab308", chip: "bg-yellow-500/15 text-yellow-300",   label: "Busy" },
  almost_full: { ring: "#f97316", chip: "bg-orange-500/15 text-orange-300",   label: "Almost full" },
  full:        { ring: "#ef4444", chip: "bg-red-500/15 text-red-300",         label: "Full" },
  overbooked:  { ring: "#dc2626", chip: "bg-red-600/25 text-red-200",         label: "Overbooked" },
};

export const TEAM_COLOR = {
  cyan: "bg-cyan-500", emerald: "bg-emerald-500", amber: "bg-amber-500",
  slate: "bg-slate-500", purple: "bg-purple-500", orange: "bg-orange-500",
  blue: "bg-blue-500", pink: "bg-pink-500",
};

export const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-white/10 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-400";

export const authHeader = () => {
  const t = localStorage.getItem("scaffoldx_token") || localStorage.getItem("DOSERNE_TOKEN");
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return iso; }
};

/** Compact DD/MM without year — for narrow columns in the planning table. */
export const fmtDateShort = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
  } catch { return iso; }
};

/** Budget calculation: hours × NOK/hour.
 *  Standard Norwegian workday = 7.5 working hours.
 *  If total_hours is provided (e.g., 20h over 2 days = 10h/day), use that directly.
 *  Otherwise, auto-compute: total_hours = members × days × 7.5.
 *  Budget: total_hours × NOK_PER_HOUR
 *  Example: 2 members × 2 days × 7.5h = 30h × 600 NOK = 18,000 NOK
 *           OR override with 20 total hours → 20 × 600 = 12,000 NOK
 */
export const HOURS_PER_DAY = 7.5;
export const NOK_PER_HOUR = 600;

export const autoHours = (workers, days) => {
  const w = parseFloat(workers) || 0;
  const d = parseFloat(days) || 0;
  return w * d * HOURS_PER_DAY;
};

export const effectiveHours = (row) => {
  const h = row?.total_hours;
  if (h != null && h !== "" && !isNaN(parseFloat(h))) return parseFloat(h);
  return autoHours(row?.planned_workers, row?.working_days);
};

export const calcBudgetNok = (workers, days, totalHoursOverride) => {
  const h = totalHoursOverride != null && totalHoursOverride !== "" && !isNaN(parseFloat(totalHoursOverride))
    ? parseFloat(totalHoursOverride)
    : autoHours(workers, days);
  return Math.round(h * NOK_PER_HOUR);
};

/** Formats a NOK amount with thousands separator, no decimals. e.g. 19200 → "19 200" */
export const fmtNok = (n) => {
  if (!n && n !== 0) return "—";
  return Math.round(n).toLocaleString("nb-NO");
};

